import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { TeamApiKeyDTO } from "@shaarp/common";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { enUS, fr } from "date-fns/locale";
import { Trash2 } from "lucide-react";
import DataTableCellTeamApiKeyCreatedBy from "./data-table-cell-team-api-key-created-by";

export const dataTableTeamApiKeysColumns = (
  t: (key: string) => string,
  onDelete?: (apiKey: TeamApiKeyDTO) => void
): ColumnDef<TeamApiKeyDTO>[] => {
  const { currentLanguage } = useTranslation();
  return [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("team.apiKeys.dataTable.columns.name")}
        />
      ),
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue("name")}</div>
      ),
    },
    {
      accessorKey: "key",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("team.apiKeys.dataTable.columns.key")}
        />
      ),
      cell: ({ row }) => (
        <div className="font-mono text-sm text-muted-foreground">
          {row.getValue("key")}
        </div>
      ),
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("team.apiKeys.dataTable.columns.createdAt")}
        />
      ),
      cell: ({ row }) => {
        const date = new Date(row.getValue("createdAt"));
        return (
          <div>
            {format(
              date,
              currentLanguage === "fr"
                ? "dd MMM y HH:mm"
                : "MMM dd, yyyy HH:mm",
              {
                locale: currentLanguage === "fr" ? fr : enUS,
              }
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "lastUsed",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("team.apiKeys.dataTable.columns.lastUsed")}
        />
      ),
      cell: ({ row }) => {
        const lastUsed = row.getValue("lastUsed") as string | null;
        return (
          <div>
            {lastUsed
              ? format(
                  new Date(lastUsed),
                  currentLanguage === "fr"
                    ? "dd MMM y HH:mm"
                    : "MMM dd, yyyy HH:mm",
                  {
                    locale: currentLanguage === "fr" ? fr : enUS,
                  }
                )
              : t("team.apiKeys.dataTable.neverUsed")}
          </div>
        );
      },
    },
    {
      accessorKey: "createdBy",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("team.apiKeys.dataTable.columns.createdBy")}
        />
      ),
      cell: ({ row }) => {
        const apiKey: TeamApiKeyDTO = row.original;
        return <DataTableCellTeamApiKeyCreatedBy apiKey={apiKey} />;
      },
    },
    ...(onDelete
      ? [
          {
            id: "actions",
            header: t("team.apiKeys.dataTable.columns.actions"),
            cell: ({ row }) => {
              const apiKey: TeamApiKeyDTO = row.original;
              return (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(apiKey)}
                  className="p-0 w-8 h-8 text-destructive hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              );
            },
            enableSorting: false,
          } as ColumnDef<TeamApiKeyDTO>,
        ]
      : []),
  ];
};
